import React from 'react';
import { useRouter, usePathname } from 'next/navigation';
import Autocomplete from '@/components/form/Autocomplete';
import setAlertValue from '@/components/Alert';
import SubmitButton from '@/components/SubmitButton';
import { TRACK_NAME } from '@/constants/site-track';
import { OptionType } from '@/types/common';
import {
  sendEvent,
  trackClickEvent,
  useIntersectionObserver,
} from '@/utils/site-track';
import {
  RESUME_EVALUATION_LOADING_ROUTER_PATH,
  RESUME_EVALUATION_ROUTER_PATH,
} from '@/constants/router-path';
import { searchByQuery, uploadEvaluationResume } from '@/api/tanzhi';

const ExpectJobTitle = (props: {
  uploading?: boolean;
  dataEleGroup?: string;
  setUploading?: React.Dispatch<React.SetStateAction<boolean>>;
  onSuccess?: (data: { id: string; task_id: string }) => void;
  resumeFile?: File | null;
  autoCompleteClassName?: string;
  submitButtonClassName?: string;
  placeHolder?: string;
  needResumeFile?: boolean;
  value?: OptionType | null;
  handleUpload?: (
    expectJobTitle: OptionType | null,
    resumeFile?: File | null,
  ) => Promise<any>;
  onNextStep?: (expectJobTitle: OptionType) => void;
  smallSize?: boolean;
  title?: React.ReactNode;
}) => {
  const {
    resumeFile,
    onSuccess,
    uploading,
    setUploading,
    placeHolder = '请输入目标岗位',
    autoCompleteClassName = 'w-full mb-12 tablet:w-[90%]',
    submitButtonClassName = 'w-full !h-13.5 !ml-auto tablet:!w-[130px]',
    dataEleGroup,
    needResumeFile = true,
    value = null,
    handleUpload = uploadEvaluationResume,
    onNextStep,
    smallSize,
    title = (
      <p
        className={`mb-4 ${smallSize ? 'text-sm' : 'text-sm font-semibold tablet:text-base'}`}
      >
        请问您的目标岗位是？<span className="text-red-600">*</span>
      </p>
    ),
  } = props;

  const [expectJobTitle, setExpectJobTitle] = React.useState<OptionType | null>(
    value,
  );
  const [observerEls, setObserverEls] = React.useState<Element[]>([]);
  const [exceptJobTitleChanged, setExceptJobTitleChanged] =
    React.useState(false);
  const expectJobTitleRef = React.useRef<HTMLDivElement | null>(null);
  const router = useRouter();
  const pathName = usePathname();

  const handleNextStep = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (needResumeFile && !resumeFile) {
      setAlertValue({ msg: '请先上传简历', severity: 'warning' });
      return;
    }
    if (!expectJobTitle || !expectJobTitle.name) {
      setAlertValue({ msg: '请填写目标岗位', severity: 'warning' });
      return;
    }
    if (dataEleGroup) {
      trackClickEvent(e);
    }
    onNextStep?.(expectJobTitle);
    setUploading?.(true);
  };

  const handleUploadResume = () => {
    if (
      !expectJobTitle ||
      !expectJobTitle.name ||
      (!resumeFile && needResumeFile)
    ) {
      return;
    }
    handleUpload(expectJobTitle, resumeFile)
      .then((res) => {
        const data = res.data;
        if (onSuccess) {
          onSuccess(data);
        } else {
          const { id } = data;
          router.push(`${RESUME_EVALUATION_LOADING_ROUTER_PATH}?id=${id}`);
        }
      })
      .catch((err) =>
        setAlertValue({
          msg: err.response.data?.detail || '诊断简历失败，请重试！',
        }),
      )
      .finally(() => {
        setUploading?.(false);
      });
  };

  React.useEffect(() => {
    if (!expectJobTitle && value) {
      setExpectJobTitle(value);
    }
  }, [value]);

  useIntersectionObserver(observerEls);

  React.useEffect(() => {
    const target = expectJobTitleRef.current;
    if (target && !observerEls.length && dataEleGroup) {
      setObserverEls([target]);
    }
    if (target && pathName === RESUME_EVALUATION_ROUTER_PATH) {
      const inputEl = target.querySelector('input');
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && inputEl) {
              inputEl.focus();
            }
          });
        },
        { threshold: 0.6 },
      );
      observer.observe(target);
      return () => observer.disconnect();
    }
  }, [expectJobTitleRef.current]);

  React.useEffect(() => {
    const target = expectJobTitleRef.current;
    if (pathName === RESUME_EVALUATION_ROUTER_PATH && target) {
      const inputEl = target.querySelector('input');
      if (value && inputEl && !exceptJobTitleChanged) {
        inputEl.style.color = '#6e6e6e';
        inputEl.style.webkitTextFillColor = '#6e6e6e';
      }
      if (inputEl && exceptJobTitleChanged) {
        inputEl.style.color = '';
        inputEl.style.webkitTextFillColor = '';
      }
    }
  }, [exceptJobTitleChanged, value]);

  return (
    <div
      data-ele-group={dataEleGroup}
      data-ele-id="expect_job_title"
      ref={expectJobTitleRef}
    >
      {title}
      <Autocomplete
        value={expectJobTitle}
        className={autoCompleteClassName}
        openAtFirstClick={false}
        getOptions={(query) => searchByQuery(query, 'name_search', false)}
        noOptionsText="抱歉，未找到您输入的职位名称，请重试。"
        placeholder={placeHolder}
        getOptionLabel={(item: OptionType | string) =>
          typeof item === 'string' ? item : item.name
        }
        onChange={(value) => {
          setExpectJobTitle({
            id: value?.id || '',
            name: value?.name || '',
          });
          sendEvent({
            name: TRACK_NAME.click,
            ele_id: 'ac_填写目标职位',
            ele_group: dataEleGroup,
          });
        }}
        handleMyInputChange={() => {
          setExceptJobTitleChanged(true);
        }}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
      />
      <div className="flex mt-auto">
        <SubmitButton
          submit={!!uploading}
          submitText="下一步"
          className={submitButtonClassName}
          handleSubmit={handleUploadResume}
          handleClick={handleNextStep}
          dataEleId="btn_目标职位下一步"
          dataEleGroup={dataEleGroup}
        />
      </div>
    </div>
  );
};

export default ExpectJobTitle;
